import { Loader2, X } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { removeGroupMember } from '../../../services/groupService';

interface RemoveGroupMemberModalProps {
  open: boolean;
  groupId: number;
  userId: string;
  memberName: string;
  onClose: () => void;
  onRemoved: () => void | Promise<void>;
  onError?: () => void;
}

export default function RemoveGroupMemberModal({
  open,
  groupId,
  userId,
  memberName,
  onClose,
  onRemoved,
  onError,
}: RemoveGroupMemberModalProps) {
  const queryClient = useQueryClient();

  const removeMutation = useMutation({
    mutationFn: () => removeGroupMember(groupId, userId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['group-details', groupId] });
      await queryClient.refetchQueries({ queryKey: ['group-details', groupId] });
      await queryClient.invalidateQueries({ queryKey: ['groups'] });
      await queryClient.refetchQueries({ queryKey: ['groups'] });
      await onRemoved();
    },
    onError: () => {
      onError?.();
    },
  });

  if (!open) {
    return null;
  }

  const isRemoving = removeMutation.isPending;

  return (
    <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/40 px-4 py-6" role="dialog" aria-modal="true">
      <div className="w-full max-w-md rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <h2 className="mb-0 text-lg font-bold text-gray-900">Remove Member</h2>
          <button
            type="button"
            onClick={onClose}
            disabled={isRemoving}
            className="flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 transition hover:bg-gray-100 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-60"
            aria-label="Close remove member confirmation"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-5 py-5">
          <p className="mb-0 text-sm leading-6 text-gray-700">
            Are you sure you want to remove
            <span className="block py-2 text-base font-semibold text-gray-900">{memberName}</span>
            from this group?
          </p>
        </div>

        <div className="flex flex-col-reverse gap-3 border-t border-gray-200 px-5 py-4 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={isRemoving}
            className="inline-flex h-10 items-center justify-center rounded-lg border border-gray-200 px-4 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => removeMutation.mutate()}
            disabled={isRemoving}
            className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-red-600 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-red-300"
          >
            {isRemoving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Removing...
              </>
            ) : (
              'Remove Member'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
